import { useEffect, useState } from 'react';
import {
	Box,
	Typography,
	Paper,
	Button,
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Switch,
	Alert,
	Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { authedApiFetch } from '../../../utils/Api';
import { useLoaderData, useParams, useNavigate } from 'react-router';
import { formatCurrency } from '../../../utils/utils';
import DealDrawer from '../components/DealDrawer';
import ConfirmationDialog from '../components/common/ConfirmationDialog';
import { StyledDataGridContainer } from '../components/common/StyledDataGridContainer';

/**
 * Loader function to fetch deals data
 */
export const loader = async ({ params }) => {
	const { tenantId } = params;

	try {
		const response = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/deals`,
			method: 'GET',
		});

		// Prepare data for the data grid - using camelCase for grid fields
		const formattedDeals = response.map((deal) => ({
			id: deal.id,
			name: deal.name,
			companyName: deal.company_info?.name || '',
			fundName: deal.fund_info?.name || '',
			owner: deal.owner_name || deal.owner_id || '',
			stage: deal.stage_name || deal.stage || '',
			// not currently passing in the stage config id, might need to add this later
			dealSize: deal.deal_size,
			currency: deal.currency,
			currencyName: deal.currency_name,
			active: deal.active,
			// Store original data for updates
			_original: {
				id: deal.id,
				company_info: deal.company_info || {},
				fund_info: deal.fund_info || {},
				owner_id: deal.owner_id,
				stage: deal.stage,
				currency: deal.currency,
				deal_size: deal.deal_size,
				active: deal.active,
			},
		}));

		return { deals: formattedDeals, error: null };
	} catch (error) {
		console.error('Error loading deals data:', error);
		return {
			deals: [],
			error: 'Failed to load deals. Please try again later.',
		};
	}
};

/**
 * Deals page for the Investing app
 * Displays information about active deals
 */
const DealsPage = () => {
	const theme = useTheme();
	const { deals, error } = useLoaderData() || { deals: [] };
	const { tenantId } = useParams();
	const navigate = useNavigate();
	const [localDeals, setLocalDeals] = useState(deals || []);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [action, setAction] = useState(null);

	// State for delete confirmation dialog
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [deleteItemId, setDeleteItemId] = useState(null);
	const [deleteItemName, setDeleteItemName] = useState('');

	// State for the action menu
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [selectedDeal, setSelectedDeal] = useState(null);
	const menuOpen = Boolean(menuAnchorEl);

	// Notification state
	const [notification, setNotification] = useState({
		open: false,
		message: '',
		severity: 'success',
		autoHideDuration: 3000,
	});

	const handleCloseNotification = () => {
		setNotification({ ...notification, open: false });
	};

	const updateErrorNotification = () => {
		setNotification({
			open: true,
			message: 'Unable to update the deal. Please try again later.',
			severity: 'error',
			autoHideDuration: 6000,
		});
	};

	// Update local state when loader data changes
	useEffect(() => {
		setLocalDeals(deals || []);
	}, [deals]);

	// Handle navigation to deal details
	const handleOpenDeal = (id) => {
		navigate(`/tenants/${tenantId}/apps/investing/deals/${id}`);
	};

	// Open delete confirmation dialog
	const openDeleteConfirm = (id, name, event) => {
		if (event) {
			event.stopPropagation(); // Prevent row selection/navigation
		}
		setDeleteItemId(id);
		setDeleteItemName(name);
		setDeleteConfirmOpen(true);
	};

	// Close delete confirmation dialog
	const closeDeleteConfirm = () => {
		setDeleteConfirmOpen(false);
		setDeleteItemId(null);
		setDeleteItemName('');
	};

	// Execute deletion after confirmation
	const handleDelete = async () => {
		if (!deleteItemId) return;

		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${deleteItemId}`,
				method: 'DELETE',
			});

			// Update local state by filtering out the deleted deal
			setLocalDeals(localDeals.filter((deal) => deal.id !== deleteItemId));
			closeDeleteConfirm();
		} catch (err) {
			console.error('Error deleting deal:', err);
			closeDeleteConfirm();
			setNotification({
				open: true,
				message: 'Unable to delete the deal. Please try again later.',
				severity: 'error',
				autoHideDuration: 6000,
			});
		}
	};

	// Handle opening the action menu
	const handleMenuOpen = (event, deal) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedDeal(deal);
	};

	// Handle closing the action menu
	const handleMenuClose = () => {
		setMenuAnchorEl(null);
		setSelectedDeal(null);
	};

	// Handle toggling active status
	const handleToggleActive = async (deal) => {
		try {
			const updatedActive = !deal.active;

			// Update on the server
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${deal.id}`,
				method: 'PATCH',
				payload: { deal: { active: updatedActive } },
			});

			// Update in the local state
			const updatedDeals = localDeals.map((d) =>
				d.id === deal.id
					? {
							...d,
							active: updatedActive,
							_original: { ...d._original, active: updatedActive },
						}
					: d
			);

			setLocalDeals(updatedDeals);
		} catch (err) {
			console.error('Error toggling deal active status:', err);
			updateErrorNotification();
		}
	};

	// Handle edit action from the menu
	const handleMenuEdit = () => {
		if (selectedDeal) {
			setAction('edit');
			setDrawerOpen(true);
		}
		// Close menu but don't reset selectedDeal as data is needed for drawer
		setMenuAnchorEl(null);
	};

	const handleMenuView = () => {
		if (selectedDeal) {
			setAction('view');
			handleOpenDeal(selectedDeal.id);
		}
		handleMenuClose();
	};

	// Handle delete action from the menu
	const handleMenuDelete = () => {
		if (selectedDeal) {
			openDeleteConfirm(selectedDeal.id, selectedDeal.name);
		}
		handleMenuClose();
	};

	// Column definitions
	const columns = [
		{ field: 'name', headerName: 'Name', flex: 1, editable: true },
		{
			field: 'companyName',
			headerName: 'Company Name',
			flex: 1,
			editable: true,
		},
		{ field: 'fundName', headerName: 'Fund Name', flex: 1, editable: true },
		{ field: 'owner', headerName: 'Owner', flex: 1, editable: false },
		{ field: 'stage', headerName: 'Stage', flex: 1, editable: false },
		{
			field: 'active',
			headerName: 'Active',
			flex: 0.7,
			editable: false,
			renderCell: (params) => (
				<Switch
					checked={!!params.row.active}
					onChange={() => handleToggleActive(params.row)}
					color='primary'
				/>
			),
		},
		{
			field: 'dealSize',
			headerName: 'Deal Size',
			flex: 1,
			editable: true,
			renderCell: (params) => {
				return formatCurrency(
					params.row.dealSize,
					params.row.currencyName || 'USD'
				);
			},
		},
		{
			field: 'actions',
			headerName: ' ',
			width: 56,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<IconButton
					onClick={(event) => handleMenuOpen(event, params.row)}
					size='small'
					color='primary'
					aria-label='more options'
				>
					<MoreVertIcon />
				</IconButton>
			),
		},
	];

	// The DataGrid needs a unique row identifier, ensure each row has proper ID
	useEffect(() => {
		const hasValidIds = localDeals.every((deal) => deal.id);
		if (!hasValidIds) {
			console.error(
				'Some deals are missing ID properties which DataGrid requires'
			);
		}
	}, [localDeals]);

	// Handle row editing
	const handleRowUpdate = async (newRow, oldRow) => {
		// Prevent API call if nothing changed
		if (JSON.stringify(newRow) === JSON.stringify(oldRow)) {
			return oldRow;
		}

		try {
			// Extract values from the data grid row and prepare for API
			const updatePayload = {
				name: newRow.name,
				stage: oldRow._original.stage,
				owner_id: oldRow._original.owner_id,
				deal_size: newRow.dealSize,
				currency: oldRow._original.currency || '$',
				company_info: {
					...(oldRow._original.company_info || {}),
					name: newRow.companyName,
				},
				fund_info: {
					...(oldRow._original.fund_info || {}),
					name: newRow.fundName,
				},
			};

			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/deals/${newRow.id}`,
				method: 'PATCH',
				payload: { deal: updatePayload },
			});

			// Update the local deals state with the new values
			const updatedDeals = localDeals.map((deal) =>
				deal.id === newRow.id
					? {
							...newRow,
							_original: {
								...oldRow._original,
								company_info: {
									...(oldRow._original.company_info || {}),
									name: newRow.companyName,
								},
								fund_info: {
									...(oldRow._original.fund_info || {}),
									name: newRow.fundName,
								},
								deal_size: newRow.dealSize,
								active: newRow.active,
							},
						}
					: deal
			);

			setLocalDeals(updatedDeals);
			return newRow;
		} catch (err) {
			console.error('Error updating deal:', err);
			updateErrorNotification();
			// Return oldRow to revert the change in the UI
			return oldRow;
		}
	};

	// Handle saving changes from the drawer
	const handleUpdateDeal = (updatedDealData) => {
		setSelectedDeal(null); // Reset selected deal after update
		if (!updatedDealData || !updatedDealData.id) return;

		// Show changes in UI
		setLocalDeals((prevDeals) =>
			prevDeals.map((deal) => {
				if (deal.id === updatedDealData.id) {
					return {
						...deal,
						id: updatedDealData.id,
						name: updatedDealData.name,
						companyName: updatedDealData.company_info?.name || '',
						fundName: updatedDealData.fund_info?.name || '',
						stage: updatedDealData.stage_name || updatedDealData.stage || '',
						dealSize: Number(updatedDealData.deal_size) || 0,
						currencyName: updatedDealData.currency_name,
						_original: updatedDealData, // Store the full original response
					};
				}
				return deal;
			})
		);
	};

	// Handle saving a new deal from the drawer
	const handleSaveDeal = (newDeal) => {
		if (!newDeal || !newDeal.id) return;

		// Format the new deal for the grid
		const formattedNewDeal = {
			id: newDeal.id,
			name: newDeal.name,
			companyName: newDeal.company_info?.name || '',
			fundName: newDeal.fund_info?.name || '',
			owner: newDeal.owner_name || newDeal.owner_id || '',
			stage: newDeal.stage_name || newDeal.stage || '',
			dealSize: Number(newDeal.deal_size) || 0,
			currency: newDeal.currency || '$',
			currencyName: newDeal.currency_name,
			active: newDeal.active,
			_original: newDeal, // Store the full original response
		};

		// Create a completely new array reference
		const updatedDeals = [...localDeals, formattedNewDeal];

		// Update the state
		setLocalDeals(updatedDeals);
	};

	// Transform data into format needed for Edit drawer
	const formatDataForDrawer = (deal) => {
		return {
			name: deal?.name,
			company_info: { name: deal?.companyName },
			fund_info: { name: deal?.fundName },
			deal_size: deal?.dealSize,
			currency: deal?.currency,
			stage: deal?.stage,
			id: deal?.id,
		};
	};

	return (
		<Box sx={{ p: 2 }}>
			<Paper
				sx={{ p: 2, mb: 2, backgroundColor: theme.palette.background.paper }}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 1,
					}}
				>
					<Typography variant='h4'>All Deals</Typography>
					<Button
						variant='contained'
						startIcon={<AddIcon />}
						onClick={() => setDrawerOpen(true)}
					>
						Add New Deal
					</Button>
				</Box>

				{error && (
					<Typography color='error' mb={2}>
						{error}
					</Typography>
				)}

				<StyledDataGridContainer>
					<DataGrid
						rows={localDeals}
						columns={columns}
						processRowUpdate={handleRowUpdate}
						experimentalFeatures={{ newEditingApi: true }}
						disableSelectionOnClick
						autoHeight
					/>
				</StyledDataGridContainer>
			</Paper>

			{/* Deal Drawer */}
			<DealDrawer
				open={drawerOpen}
				onClose={() => {
					setDrawerOpen(false);
					setSelectedDeal(null);
				}}
				onSave={(updatedDealData) =>
					action === 'edit'
						? handleUpdateDeal(updatedDealData)
						: handleSaveDeal()
				}
				editMode={action === 'edit'}
				tenantId={tenantId}
				dealData={action === 'edit' ? formatDataForDrawer(selectedDeal) : null}
			/>

			{/* Action Menu */}
			<Menu
				anchorEl={menuAnchorEl}
				open={menuOpen}
				onClose={handleMenuClose}
				PaperProps={{
					elevation: 2,
					sx: {
						minWidth: 150,
						maxWidth: 200,
					},
				}}
			>
				<MenuItem onClick={handleMenuView}>
					<ListItemIcon>
						<VisibilityIcon fontSize='small' />
					</ListItemIcon>
					<ListItemText>View</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleMenuEdit}>
					<ListItemIcon>
						<EditIcon fontSize='small' />
					</ListItemIcon>
					<ListItemText>Edit</ListItemText>
				</MenuItem>
				<MenuItem onClick={handleMenuDelete}>
					<ListItemIcon>
						<DeleteIcon fontSize='small' color='error' />
					</ListItemIcon>
					<ListItemText
						primary='Delete'
						primaryTypographyProps={{ color: 'error' }}
					/>
				</MenuItem>
			</Menu>

			{/* Delete Confirmation Dialog */}
			<ConfirmationDialog
				open={deleteConfirmOpen}
				handleConfirm={handleDelete}
				handleCancel={closeDeleteConfirm}
				title='Delete Deal'
				message={`Are you sure you want to delete the deal "${deleteItemName}"? This action cannot be undone.`}
				confirmActionButtonLabel='Delete'
				confirmActionButtonColor='error'
				cancelActionButtonColor='secondary'
			/>

			<Snackbar
				open={notification.open}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={notification.autoHideDuration}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant='filled'
					sx={{ width: '100%' }}
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default DealsPage;
